import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useMarketPricesUi, shiftDayIso } from '@/hooks/useMarketPricesUi';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  loadLinesDay, indices, todayKyiv, useLineColors, f0, f2, pct, dmy, hh, dev, DASH,
  type LinesDay, type LineKey, type DayMetrics,
} from './pricesData';

/**
 * РДН «За добу» — одна часова шкала: факт ціни та лінії прогнозу на одному графіку (як Base/ECMWF у генерації).
 *  · Факт — суцільна (--series-3); BASE — суцільна сіра з межами 10–90 %; EXPRO — штрихпунктир; «Як учора» — пунктир.
 *  · Кожну лінію можна сховати кнопкою-чипом над графіком; спливне вікно показує всі значення години.
 *  · Для минулих діб — той прогноз, що був «заморожений» до подачі заявок (D−1 12:00), тож видно, як він справдився.
 * Плитки — за макетом /tg (BASE · середньозважена · PEAK · OFFPEAK · точність), цифри кольором своїх ліній.
 */

type Vis = { fact: boolean; base: boolean; expro: boolean; naive: boolean; band: boolean };

function Tile({ title, fact, base, expro, hasFact, colors }: {
  title: string; fact: number | null; base: number | null; expro: number | null; hasFact: boolean;
  colors: ReturnType<typeof useLineColors>;
}) {
  const { t } = useTranslation();
  const ref = base ?? expro;
  const d = hasFact ? dev(fact, ref) : null;
  return (
    <div className="tile p-3.5 flex flex-col" style={{ minHeight: 132 }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold tracking-wide uppercase truncate" style={{ color: 'var(--text-muted)' }}>{title}</div>
        {hasFact && (
          <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-md shrink-0"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text-muted)' }}>{t('pv2_badge_fact')}</span>
        )}
      </div>
      <div className="flex items-baseline gap-2 mt-1.5" style={{ minHeight: 30 }}>
        {hasFact
          ? <span className="font-display text-[24px] leading-none font-semibold" style={{ color: colors.fact, fontVariantNumeric: 'tabular-nums' }}>{f2(fact)}</span>
          : <span className="block rounded" style={{ width: 22, height: 3, background: 'var(--line-strong)', alignSelf: 'center' }} />}
        <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{t('pv2_unit')}</span>
      </div>
      <div className="mt-auto pt-2 flex flex-col gap-0.5 text-[12px]">
        <div className="flex items-baseline justify-between">
          <span style={{ color: 'var(--text-muted)' }}>BASE</span>
          <span className="font-display text-[16px] font-semibold" style={{ color: base == null ? 'var(--text-dim)' : colors.base, fontVariantNumeric: 'tabular-nums' }}>{f2(base)}</span>
        </div>
        {expro != null && (
          <div className="flex items-baseline justify-between">
            <span style={{ color: 'var(--text-muted)' }}>EXPRO</span>
            <span className="font-display text-[16px] font-semibold" style={{ color: colors.expro, fontVariantNumeric: 'tabular-nums' }}>{f2(expro)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between">
          <span style={{ color: 'var(--text-muted)' }}>{t('pv2_deviation')}</span>
          <span className="font-semibold" style={{ color: d == null ? 'var(--text-dim)' : 'var(--text)' }}>{pct(d, 2)}</span>
        </div>
      </div>
    </div>
  );
}

/** Точність за добу: точні години BASE проти «як учора» (чесна мірка — завжди поруч із наївним базисом). */
function AccuracyTile({ m, naive, tol, naiveLabel }: { m: DayMetrics | null; naive: DayMetrics | null; tol: { rel: number; abs: number }; naiveLabel: string }) {
  const { t } = useTranslation();
  return (
    <div className="tile p-3.5 flex flex-col" style={{ minHeight: 132 }}>
      <div className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>{t('pv2_accuracy')}</div>
      <div className="flex items-baseline gap-1.5 mt-1.5" style={{ minHeight: 30 }}>
        {m
          ? <><span className="font-display text-[24px] leading-none font-semibold" style={{ color: 'var(--text)' }}>{m.accurate}</span>
              <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{t('pv2_of_hours', { n: m.total })}</span></>
          : <span className="block rounded" style={{ width: 22, height: 3, background: 'var(--line-strong)', alignSelf: 'center' }} />}
      </div>
      <div className="mt-auto pt-2 flex flex-col gap-0.5 text-[12px]">
        <div className="flex items-baseline justify-between">
          <span style={{ color: 'var(--text-muted)' }}>{naiveLabel}</span>
          <span className="font-semibold" style={{ color: 'var(--text)' }}>{naive ? t('pv2_of_hours_short', { a: naive.accurate, n: naive.total }) : '—'}</span>
        </div>
        <div className="text-[10.5px] leading-snug" style={{ color: 'var(--text-dim)' }}>
          {m || naive ? t('pv2_accurate_hint', { rel: Math.round(tol.rel * 100), abs: tol.abs }) : t('pv2_after_fact')}
        </div>
      </div>
    </div>
  );
}

function Chip({ on, color, label, onClick, dash, disabled, title }: {
  on: boolean; color: string; label: string; onClick: () => void; dash?: string; disabled?: boolean; title?: string;
}) {
  return (
    <button type="button" onClick={disabled ? undefined : onClick} title={title} disabled={disabled}
      className="flex items-center justify-center gap-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap"
      // як чипи Base/ECMWF у прогнозі генерації: тло й рамка — колір своєї лінії
      style={{ minWidth: 120, padding: '6px 12px', background: color + '1F', color: 'var(--text)', border: '1px solid ' + color + '66',
               opacity: disabled ? 0.35 : on ? 1 : 0.45, cursor: disabled ? 'not-allowed' : 'pointer' }}>
      <svg width="18" height="6" aria-hidden><line x1="1" y1="3" x2="17" y2="3" stroke={color} strokeWidth="2.5" strokeDasharray={dash} strokeLinecap="round" /></svg>
      {label}
    </button>
  );
}

export default function RdnPricesDay({ date, view, controls }: { date: string; view: 'chart' | 'table'; controls: ReactNode }) {
  const { t } = useTranslation();
  const c = useLineColors();
  const { setRange, setDate } = useMarketPricesUi();
  const [d, setD] = useState<LinesDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [vis, setVis] = useState<Vis>({ fact: true, base: true, expro: true, naive: false, band: true });

  // Межі навігації: від першої доби з фактом до +31 доби (BASE v1.0 прогнозує D+1…D+31; після D+8 — оцінка рівня);
  // за замовчуванням — остання доба з фактом.
  useEffect(() => {
    let dead = false;
    authFetch(`${API_BASE_URL}/market/dam-summary`).then((r) => (r.ok ? r.json() : null))
      .then((s: { tradeDate?: string | null; dataRange?: { min: string | null } } | null) => {
        if (dead || !s) return;
        setRange(s.dataRange?.min ?? null, shiftDayIso(todayKyiv(), 31));
        if (!date && s.tradeDate) setDate(s.tradeDate);
      }).catch(() => {});
    return () => { dead = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!date) return;
    let dead = false;
    setLoading(true);
    loadLinesDay(date).then((x) => { if (!dead) setD(x); }).catch(() => { if (!dead) setD(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [date]);

  const hours = d?.hours ?? [];
  const hasFactNow = !!d?.hasFact || hours.some((h) => h.actual != null);
  const has = { fact: hasFactNow, base: !!d?.lines.base, expro: !!d?.lines.expro,
                naive: hasFactNow && hours.some((h) => h.naive != null) };

  // Індекси для плиток: факт; лінії — з P50; середньозважена прогнозу — за обсягами доби «як учора» (модель обсягів не прогнозує).
  const idx = useMemo(() => {
    // ваги прогнозу — обсяги «як учора» (відомі на момент прогнозу), не факт цієї доби
    const of = (k: LineKey) => (d?.lines[k] ? indices(hours.map((h) => ({ clock: h.clock, price: h[k]?.p50 ?? null, volume: h.naiveVolume }))) : null);
    return { fact: has.fact ? indices(hours.map((h) => ({ clock: h.clock, price: h.actual, volume: h.volume }))) : null, base: of('base'), expro: of('expro') };
  }, [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => hours.map((h) => ({
    hh: String(h.clock).padStart(2, '0'), clock: h.clock, hour: h.hour,
    fact: h.actual, volume: h.volume, naive: h.naive,
    base: h.base?.p50 ?? null, baseBand: h.base && h.base.p10 != null && h.base.p90 != null ? [h.base.p10, h.base.p90] as [number, number] : null,
    expro: h.expro?.p50 ?? null, exproBand: h.expro && h.expro.p10 != null && h.expro.p90 != null ? [h.expro.p10, h.expro.p90] as [number, number] : null,
  })), [d]); // eslint-disable-line react-hooks/exhaustive-deps

  const [lo, hi, ticks] = useMemo(() => {
    const vals = rows.flatMap((r) => [
      vis.fact ? r.fact : null, vis.base ? r.base : null, vis.expro ? r.expro : null, vis.naive ? r.naive : null,
      ...(vis.band && vis.base && r.baseBand ? r.baseBand : []), ...(vis.band && vis.expro && r.exproBand ? r.exproBand : []),
    ]).filter((v): v is number => v != null);
    if (!vals.length) return [0, 10000, [0, 2000, 4000, 6000, 8000, 10000]] as const;
    const l = Math.max(0, Math.floor((Math.min(...vals) - 300) / 1000) * 1000);
    const h = Math.ceil((Math.max(...vals) + 300) / 1000) * 1000;
    const step = h - l > 10000 ? 2000 : 1000;
    return [l, h, Array.from({ length: Math.floor((h - l) / step) + 1 }, (_, i) => l + i * step)] as const;
  }, [rows, vis]);

  const runLabel = (k: LineKey) => {
    const r = d?.lines[k]?.run;
    if (!r) return t('pv2_no_run');
    // ретро — прогін, порахований заднім числом за даними до asof (історія моделі), не живий прогноз того дня
    return t('pv2_run_from', { d: dmy(r.asof), h: r.horizon, model: r.model }) + (r.retro ? ` · ${t('pv2_retro')}` : '')
      + (r.horizon >= 8 ? ` · ${t('pv2_level_estimate')}` : '');
  };

  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const r = payload[0].payload as (typeof rows)[number];
    const line = (label: string, v: number | null, color: string, band?: [number, number] | null) => (
      <div className="flex items-baseline justify-between gap-4">
        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {f0(v)}{band ? <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}> · {f0(band[0])}–{f0(band[1])}</span> : null}
        </span>
      </div>
    );
    return (
      <div style={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, padding: '8px 12px', fontSize: 12, color: c.text, minWidth: 220 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{dmy(date)} · {hh(r.clock)}–{hh((r.clock + 1) % 24)}</div>
        {line(t('pv2_line_fact'), r.fact, c.fact)}
        {has.base && line('BASE', r.base, c.base, r.baseBand)}
        {has.expro && line('EXPRO', r.expro, c.expro, r.exproBand)}
        {has.naive && line(naiveLabel, r.naive, c.naive)}
        {r.fact != null && r.base != null && (
          <div className="flex justify-between gap-4 mt-1 pt-1" style={{ borderTop: `1px solid ${c.grid}` }}>
            <span style={{ color: 'var(--text-muted)' }}>{t('pv2_err_base')}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{f0(r.base - r.fact)} {t('pv2_uah')}</span>
          </div>
        )}
        {r.volume != null && <div className="flex justify-between gap-4"><span style={{ color: 'var(--text-muted)' }}>{t('pv2_volume')}</span><span>{f0(r.volume)} {t('pv2_mwh')}</span></div>}
      </div>
    );
  };

  // Базис порівняння — ціни тієї ж години попередньої доби; підпис — конкретною датою («Ціна 28.10»), бо «D−1» плутався
  // з навігацією (там D відраховується від сьогодні). Показуємо лише на добах з фактом — там він і потрібен для перевірки.
  const naiveLabel = d ? t('pv2_naive_date', { d: dmy(d.naive.day).slice(0, 5) }) : t('pv2_line_naive');
  const empty = !loading && !hours.length;
  const lineQ = (k: LineKey | 'naive') => {
    if (!d) return null;
    if (k === 'naive') return { m: d.naive.metrics, bess: d.naive.bess ?? null };
    const l = d.lines[k];
    return l ? { m: l.metrics, bess: l.bess } : null;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" style={{ opacity: loading && !d ? 0.5 : 1, transition: 'opacity .15s' }}>
        <Tile title="BASE" fact={idx.fact?.base ?? null} base={idx.base?.base ?? null} expro={idx.expro?.base ?? null} hasFact={has.fact} colors={c} />
        <Tile title={t('pv2_weighted')} fact={idx.fact?.weighted ?? null} base={idx.base?.weighted ?? null} expro={idx.expro?.weighted ?? null} hasFact={has.fact} colors={c} />
        <Tile title="PEAK" fact={idx.fact?.peak ?? null} base={idx.base?.peak ?? null} expro={idx.expro?.peak ?? null} hasFact={has.fact} colors={c} />
        <Tile title="OFFPEAK" fact={idx.fact?.offpeak ?? null} base={idx.base?.offpeak ?? null} expro={idx.expro?.offpeak ?? null} hasFact={has.fact} colors={c} />
        <AccuracyTile m={d?.lines.base?.metrics ?? null} naive={d?.naive.metrics ?? null} tol={d?.tolerance ?? { rel: 0.15, abs: 300 }} naiveLabel={naiveLabel} />
      </div>

      <div className="flex justify-end">{controls}</div>

      <div className="tile p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-display text-[15px] font-semibold" style={{ color: 'var(--text)' }}>{t('pv2_hourly_title')}</div>
            <div className="text-[12px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {has.fact ? t('pv2_sub_fact') : t('pv2_sub_nofact')}{has.base ? ` · BASE ${runLabel('base')}` : ''}{has.expro ? ` · EXPRO ${runLabel('expro')}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Chip on={vis.fact} color={c.fact} label={t('pv2_line_fact')} disabled={!has.fact} onClick={() => setVis((v) => ({ ...v, fact: !v.fact }))} />
            <Chip on={vis.base} color={c.base} label="BASE" disabled={!has.base} title={runLabel('base')} onClick={() => setVis((v) => ({ ...v, base: !v.base }))} />
            <Chip on={vis.expro} color={c.expro} label="EXPRO" dash={DASH.expro} disabled={!has.expro}
              title={has.expro ? runLabel('expro') : t('pv2_expro_pending')} onClick={() => setVis((v) => ({ ...v, expro: !v.expro }))} />
            <Chip on={vis.naive} color={c.naive} label={naiveLabel} dash={DASH.naive} disabled={!has.naive}
              title={d ? t('pv2_naive_title', { d: dmy(d.naive.day) }) : undefined} onClick={() => setVis((v) => ({ ...v, naive: !v.naive }))} />
            <button type="button" onClick={() => setVis((v) => ({ ...v, band: !v.band }))} disabled={!has.base && !has.expro}
              className="inline-flex items-center gap-1.5 text-xs cursor-pointer" title={t('pv2_band_title')}
              style={{ opacity: vis.band ? 1 : 0.45, background: 'none', border: 'none', color: 'var(--text-muted)', padding: '6px 4px' }}>
              <span style={{ width: 14, height: 10, borderRadius: 2, background: `linear-gradient(90deg, ${c.base}55 50%, ${c.expro}55 50%)` }} />{t('pv2_band')}
            </button>
          </div>
        </div>

        {empty ? (
          <div className="flex items-center justify-center text-xs text-center px-4" style={{ color: 'var(--text-muted)', height: 360 }}>
            {t('pv2_empty', { d: dmy(date) })}
          </div>
        ) : loading && !d ? (
          <div className="flex items-center justify-center text-xs" style={{ color: 'var(--text-muted)', height: 360 }}>{t('pv2_loading')}</div>
        ) : view === 'chart' ? (
          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="hh" tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} interval={0} />
                <YAxis domain={[lo, hi]} ticks={[...ticks]} width={56} tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => f0(v)} />
                <Tooltip content={<Tip />} />
                {vis.band && vis.base && has.base && (
                  <Area type="monotone" dataKey="baseBand" stroke="none" fill={c.base} fillOpacity={0.10} activeDot={false} connectNulls isAnimationActive={false} />
                )}
                {vis.band && vis.expro && has.expro && (
                  <Area type="monotone" dataKey="exproBand" stroke={c.expro} strokeOpacity={0.35} strokeDasharray="2 3" fill={c.expro} fillOpacity={0.10} activeDot={false} connectNulls isAnimationActive={false} />
                )}
                {vis.naive && <Line type="monotone" dataKey="naive" stroke={c.naive} strokeWidth={1.5} strokeDasharray={DASH.naive} dot={false} activeDot={false} connectNulls isAnimationActive={false} />}
                {vis.base && <Line type="monotone" dataKey="base" stroke={c.base} strokeWidth={2.25} dot={false} activeDot={{ r: 4, fill: c.base }} connectNulls />}
                {vis.expro && <Line type="monotone" dataKey="expro" stroke={c.expro} strokeWidth={2.5} strokeDasharray={DASH.expro} dot={false} activeDot={{ r: 4, fill: c.expro }} connectNulls />}
                {vis.fact && <Line type="monotone" dataKey="fact" stroke={c.fact} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ border: '1px solid var(--line)', borderRadius: 12 }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 900 }}>
              <tbody>
                {([
                  [t('pv2_hour'), rows.map((r) => r.hh), 'head', ''],
                  [t('pv2_row_fact'), rows.map((r) => f0(r.fact)), 'strong', c.fact],
                  ...(has.base ? [
                    [t('pv2_row_base'), rows.map((r) => f0(r.base)), 'strong', c.base],
                    [t('pv2_row_base_band'), rows.map((r) => (r.baseBand ? `${f0(r.baseBand[0])}–${f0(r.baseBand[1])}` : '—')), '', ''],
                    [t('pv2_row_dev_base'), rows.map((r) => pct(dev(r.fact, r.base), 1)), '', ''],
                  ] : []),
                  ...(has.expro ? [
                    [t('pv2_row_expro'), rows.map((r) => f0(r.expro)), 'strong', c.expro],
                    [t('pv2_row_dev_expro'), rows.map((r) => pct(dev(r.fact, r.expro), 1)), '', ''],
                  ] : []),
                  ...(has.naive ? [[naiveLabel, rows.map((r) => f0(r.naive)), '', '']] : []),
                  [t('pv2_row_volume'), rows.map((r) => f0(r.volume)), '', ''],
                ] as [string, string[], string, string][]).map(([label, cells, kind, color]) => (
                  <tr key={label}>
                    <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', background: kind === 'head' ? 'var(--surface-2)' : 'var(--surface-solid)', position: 'sticky', left: 0, minWidth: 190, borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', whiteSpace: 'nowrap' }}>{label}</td>
                    {cells.map((cell, i) => (
                      <td key={i} style={{ padding: '8px 10px', fontSize: 13, textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)',
                        background: kind === 'head' ? 'var(--surface-2)' : undefined, fontWeight: kind ? 600 : 400,
                        color: color || (kind === 'head' ? 'var(--text-muted)' : 'var(--text)') }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Якість прогнозу за добу — кожна лінія проти «як учора», у годинах і в гривнях (цикл УЗЕ Лучок). */}
      {d && has.fact && (d.lines.base || d.lines.expro) && (
        <div className="tile p-4 sm:p-5">
          <div className="font-display text-[15px] font-semibold" style={{ color: 'var(--text)' }}>{t('pv2_quality_title')}</div>
          <div className="text-[12px] mt-0.5 mb-3" style={{ color: 'var(--text-muted)' }}>{t('pv2_quality_sub')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)' }}>
                  {[t('pv2_q_line'), t('pv2_q_mae'), t('pv2_q_accurate'), t('pv2_q_band'), t('pv2_q_bess')].map((h, i) => (
                    <th key={h} className="font-semibold py-2 px-2" style={{ textAlign: i ? 'right' : 'left', borderBottom: '1px solid var(--line)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(['base', 'expro', 'naive'] as const).map((k) => {
                  const q = lineQ(k);
                  if (!q || !q.m) return null;
                  const color = k === 'naive' ? 'var(--text-muted)' : c[k];
                  const b = q.bess;
                  return (
                    <tr key={k}>
                      <td className="py-2 px-2 font-semibold" style={{ color, borderBottom: '1px solid var(--line)' }}>{k === 'naive' ? naiveLabel : k.toUpperCase()}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ borderBottom: '1px solid var(--line)' }}>{f0(q.m.mae)} {t('pv2_uah')}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ borderBottom: '1px solid var(--line)' }}>{q.m.accurate} / {q.m.total}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ borderBottom: '1px solid var(--line)' }}>{q.m.inCorridor != null && k !== 'naive' ? `${q.m.inCorridor} / ${q.m.total}` : '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ borderBottom: '1px solid var(--line)' }}>
                        {b?.realized != null && b.ideal ? `${f0(b.realized / 1000)} ${t('pv2_k_uah')} · ${Math.round(b.realized / b.ideal * 100)} %` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--text-dim)' }}>
            {t('pv2_quality_note', { rel: Math.round((d.tolerance?.rel ?? 0.15) * 100), abs: d.tolerance?.abs ?? 300 })}
          </div>
        </div>
      )}
    </div>
  );
}
